import { RailsAPIBase, type paths } from "../index.client";
import type { Simplify } from "type-fest";

/** DeployTaskRecord derived from RSwag-generated OpenAPI types (GET /api/v1/deploys/{deploy_id}/tasks) */
export type DeployTaskRecord =
  paths["/api/v1/deploys/{deploy_id}/tasks"]["get"]["responses"][200]["content"]["application/json"]["tasks"][number];

/** Response from GET /api/v1/deploys/{deploy_id}/tasks */
export type DeployTasksResponse =
  paths["/api/v1/deploys/{deploy_id}/tasks"]["get"]["responses"][200]["content"]["application/json"];

/** Parameters for POST /api/v1/deploys/{deploy_id}/tasks/bulk */
export type CreateDeployTaskParams = NonNullable<
  paths["/api/v1/deploys/{deploy_id}/tasks/bulk"]["post"]["requestBody"]
>["content"]["application/json"]["tasks"][number];

/** Parameters for PATCH /api/v1/deploys/{deploy_id}/tasks/{task_name} */
export type UpdateDeployTaskParams = NonNullable<
  paths["/api/v1/deploys/{deploy_id}/tasks/{task_name}"]["patch"]["requestBody"]
>["content"]["application/json"];

/**
 * Service for interacting with the Rails Deploy Tasks API.
 * Used by Langgraph to sync task state to the database for observability.
 *
 * All methods are fire-and-forget safe — callers should wrap in try/catch
 * so task tracking failures don't block deploy execution.
 */
export class DeployTaskAPIService extends RailsAPIBase {
  constructor(options: Simplify<ConstructorParameters<typeof RailsAPIBase>[0]>) {
    super(options);
  }

  /**
   * Fetches all tasks for a deploy, along with the deploy's status.
   * Used by frontend polling to track deploy progress.
   */
  async getTasks(deployId: number): Promise<DeployTasksResponse> {
    const client = await this.getClient();
    const response = await client.GET(
      `/api/v1/deploys/${deployId}/tasks` as any, {} as any
    );

    if (response.error) {
      throw new Error(`Failed to get deploy tasks: ${JSON.stringify(response.error)}`);
    }

    return response.data as DeployTasksResponse;
  }

  /**
   * Creates all tasks for a deploy in one call.
   * Creates multiple tasks for a deploy. Used for Langgraph→Rails task sync.
   * Idempotent: skips tasks that already exist.
   */
  async bulkCreate(
    deployId: number,
    tasks: CreateDeployTaskParams[]
  ): Promise<{ tasks: DeployTaskRecord[] }> {
    const client = await this.getClient();
    const response = await client.POST(
      `/api/v1/deploys/${deployId}/tasks/bulk` as any,
      { body: { tasks } as any }
    );

    if (response.error) {
      throw new Error(`Failed to create deploy tasks: ${JSON.stringify(response.error)}`);
    }

    return response.data as { tasks: DeployTaskRecord[] };
  }

  /**
   * Fetches a single task by name.
   * Used to read error details when a deploy fails.
   */
  async getTask(
    deployId: number,
    taskName: string
  ): Promise<DeployTaskRecord> {
    const client = await this.getClient();
    const response = await client.GET(
      `/api/v1/deploys/${deployId}/tasks/${taskName}` as any, {} as any
    );

    if (response.error) {
      throw new Error(`Failed to get deploy task: ${JSON.stringify(response.error)}`);
    }

    return response.data as DeployTaskRecord;
  }

  /**
   * Updates a task's status by name.
   * Called by taskWrapper when a task transitions.
   */
  async updateTask(
    deployId: number,
    taskName: string,
    updates: UpdateDeployTaskParams
  ): Promise<DeployTaskRecord> {
    const client = await this.getClient();
    const response = await client.PATCH(
      `/api/v1/deploys/${deployId}/tasks/${taskName}` as any,
      { body: updates as any }
    );

    if (response.error) {
      throw new Error(`Failed to update deploy task: ${JSON.stringify(response.error)}`);
    }

    return response.data as DeployTaskRecord;
  }
}
